import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
import IntroOverlay from "../components/IntroOverlay";

// ✅ Sections below-the-fold : lazy-loaded pour réduire le JS initial
const GoogleSimulationSection = lazy(() => import("../components/GoogleSimulationSection"));
const ProblemSolutionSection = lazy(() => import("../components/ProblemSolutionSection"));
const ServicesSection = lazy(() => import("../components/ServicesSection"));
const PortfolioSection = lazy(() => import("../components/PortfolioSection"));
const ContactSection = lazy(() => import("../components/ContactSection"));
const FAQSection = lazy(() => import("../components/FAQSection"));
const GoogleReviewsSection = lazy(() => import("../components/TestimonialsSection"));
const Footer = lazy(() => import("../components/Footer"));

// Variable de configuration pour désactiver l'intro (tests Lighthouse)
const DISABLE_INTRO = process.env.REACT_APP_DISABLE_INTRO === 'true';

// Fallback invisible pour les sections lazy (pas de spinner = pas de CLS)
const SectionFallback = () => <div style={{ minHeight: '100px' }} />;

const HomePage = () => {
  const location = useLocation();

  // ✅ Si on arrive depuis une autre page avec une cible de scroll (hash OU state),
  //    on saute l'intro animation pour aller directement à la section demandée.
  const initialTargetId =
    (typeof window !== 'undefined' &&
      ((location.state && location.state.scrollTo) ||
        (window.location.hash ? window.location.hash.replace('#', '') : null))) ||
    null;

  // State global : l'intro est-elle terminée ?
  // ✅ Joue UNE SEULE FOIS par session : sessionStorage empêche le rejeu
  //    lors des navigations internes (retour accueil depuis /services, /contact, pages villes…)
  const [introFinished, setIntroFinished] = useState(() => {
    if (DISABLE_INTRO || initialTargetId) return true;
    if (typeof window !== 'undefined' && window.sessionStorage.getItem('vyntraweb_intro_seen') === '1') {
      return true;
    }
    return false;
  });

  // ✅ Dès que l'intro se termine, on mémorise le flag pour la session
  const handleIntroComplete = () => {
    try {
      window.sessionStorage.setItem('vyntraweb_intro_seen', '1');
    } catch (_) {
      /* sessionStorage peut être indisponible (mode privé strict) */
    }
    setIntroFinished(true);
  };

  useEffect(() => {
    // Désactiver la restauration automatique du scroll
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // ✅ Cible de scroll : hash de l'URL OU state passé par Header (navigation depuis page SEO)
    const targetId =
      (location.state && location.state.scrollTo) ||
      (window.location.hash ? window.location.hash.replace('#', '') : null);

    if (targetId) {
      // Les sections sont lazy-loaded — on retente plusieurs fois jusqu'à ce que la cible soit montée
      let attempts = 0;
      const tryScroll = () => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        attempts += 1;
        if (attempts < 20) setTimeout(tryScroll, 150); // jusqu'à ~3 s
      };
      setTimeout(tryScroll, 100);
    } else {
      // Pas de hash ni de state → on remet en haut
      window.scrollTo(0, 0);
    }

    return () => {
      // Restaurer le comportement par défaut au démontage
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, [location.state, location.key]);

  // Données structurées JSON-LD optimisées SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      // Organization
      {
        "@type": "Organization",
        "@id": "https://vyntraweb.fr/#organization",
        "name": "VyntraWeb",
        "alternateName": "Vyntra Web",
        "url": "https://vyntraweb.fr",
        "logo": {
          "@type": "ImageObject",
          "url": "https://vyntraweb.fr/logo.png",
          "width": 250,
          "height": 60
        },
        "founder": {
          "@type": "Person",
          "name": "Enzo Persch-Chiugi"
        },
        "sameAs": [
          "https://www.tiktok.com/@vyntraweb",
          "https://www.instagram.com/vyntraweb",
          "https://www.facebook.com/VyntraWeb"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "vyntraweb@gmail.com",
          "telephone": "+33-6-XX-XX-XX-XX",
          "contactType": "customer service",
          "availableLanguage": ["French"],
          "areaServed": "FR"
        }
      },
      // LocalBusiness - SEO Local
      {
        "@type": "LocalBusiness",
        "@id": "https://vyntraweb.fr/#localbusiness",
        "name": "VyntraWeb - Agence Web Thionville",
        "image": "https://vyntraweb.fr/logo.png",
        "description": "Agence de création de sites web pour commerces locaux à Thionville, Metz, Luxembourg. Spécialisée restaurants, pizzerias, coiffeurs, snacks.",
        "url": "https://vyntraweb.fr",
        "telephone": "+33-6-XX-XX-XX-XX",
        "email": "vyntraweb@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Thionville",
          "addressRegion": "Grand Est",
          "postalCode": "57100",
          "addressCountry": "FR"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 49.3567,
          "longitude": 6.1686
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Thionville"
          },
          {
            "@type": "City",
            "name": "Metz"
          },
          {
            "@type": "City",
            "name": "Luxembourg"
          },
          {
            "@type": "City",
            "name": "Yutz"
          },
          {
            "@type": "City",
            "name": "Hayange"
          },
          {
            "@type": "City",
            "name": "Fameck"
          }
        ],
        "priceRange": "€€",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      },
      // WebSite
      {
        "@type": "WebSite",
        "@id": "https://vyntraweb.fr/#website",
        "url": "https://vyntraweb.fr",
        "name": "VyntraWeb - Agence Web pour Commerces Locaux",
        "description": "Création de sites web professionnels pour restaurants, coiffeurs et commerces locaux à Thionville, Metz et Luxembourg",
        "publisher": {
          "@id": "https://vyntraweb.fr/#organization"
        },
        "inLanguage": "fr-FR",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://vyntraweb.fr/?s={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      // Service avec offres
      {
        "@type": "Service",
        "@id": "https://vyntraweb.fr/#service",
        "serviceType": "Création de sites web",
        "provider": {
          "@id": "https://vyntraweb.fr/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "France"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Offres création de sites web",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Offre Pro",
                "description": "Site professionnel pour être présent en ligne avec hébergement et maintenance inclus"
              },
              "price": "49",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "49",
                "priceCurrency": "EUR",
                "billingDuration": "P1M"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Offre Premium",
                "description": "Site optimisé SEO qui apporte des clients chaque mois avec référencement local avancé"
              },
              "price": "79",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "79",
                "priceCurrency": "EUR",
                "billingDuration": "P1M"
              }
            }
          ]
        }
      },
      // FAQ Schema
      {
        "@type": "FAQPage",
        "@id": "https://vyntraweb.fr/#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Combien de temps faut-il pour créer mon site ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Jamais plus d'une semaine après le brief initial. Certains projets simples sont livrés en 48h."
            }
          },
          {
            "@type": "Question",
            "name": "Mon site sera-t-il visible sur Google ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Oui, et optimisé pour apparaître dans les premiers résultats. Un client tape 'pizzeria Thionville' et tombe directement sur votre site professionnel."
            }
          },
          {
            "@type": "Question",
            "name": "Est-ce que je dois avoir un nom de domaine ou un hébergeur ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Non, tout est inclus. Le nom de domaine et l'hébergement premium sont compris dans l'abonnement."
            }
          },
          {
            "@type": "Question",
            "name": "Quelle est la différence entre les offres Pro et Premium ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "L'offre Pro (49€/mois) inclut un site professionnel avec hébergement. L'offre Premium (79€/mois) ajoute le SEO avancé, les avis Google intégrés et la commande/réservation en ligne."
            }
          },
          {
            "@type": "Question",
            "name": "Y a-t-il des frais cachés ou un engagement ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Aucun frais caché, jamais. Les offres sont résiliables à tout moment sans engagement."
            }
          }
        ]
      },
      // BreadcrumbList
      {
        "@type": "BreadcrumbList",
        "@id": "https://vyntraweb.fr/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Accueil",
            "item": "https://vyntraweb.fr/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://vyntraweb.fr/services"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Portfolio",
            "item": "https://vyntraweb.fr/portfolio"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "Contact",
            "item": "https://vyntraweb.fr/contact"
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title="Création site internet & site web Thionville | VyntraWeb"
        description="VyntraWeb crée des sites internet modernes à Thionville pour restaurants, snacks, coiffeurs et commerces locaux. Site rapide, mobile et optimisé Google pour attirer plus de clients."
        canonical="https://vyntraweb.fr/"
        structuredData={structuredData}
      />
      
      {/* IntroOverlay en premier — bloque visuellement le site pendant la cinématique */}
      {!introFinished && (
        <IntroOverlay onComplete={handleIntroComplete} />
      )}
      
      {/* Site complet toujours dans le DOM (SEO) — hero attend introFinished pour s'animer */}
      <div className="min-h-screen bg-slate-900">
        <Header />
        <div className="space-y-0">
          <HeroSection introFinished={introFinished} />
          <Suspense fallback={<SectionFallback />}>
            <ProblemSolutionSection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <GoogleSimulationSection />
          </Suspense>
          <div className="h-16"></div>
          <Suspense fallback={<SectionFallback />}>
            <ServicesSection />
          </Suspense>
          <div className="h-12"></div>
          <Suspense fallback={<SectionFallback />}>
            <PortfolioSection />
          </Suspense>
          <div className="h-12"></div>
          <section className="py-16 md:py-20 bg-slate-950/50">
            <div className="container mx-auto px-6">
              <div className="max-w-5xl mx-auto rounded-3xl border border-cyan-400/20 bg-slate-900/70 p-8 md:p-10 shadow-2xl shadow-cyan-500/5 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#F87171]/10 rounded-full blur-3xl"></div>
                <div className="relative z-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
                  <div>
                    <span className="text-cyan-400 font-semibold uppercase tracking-wider text-sm">
                      SEO local Moselle
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-5">
                      Agence web à Thionville pour commerces locaux
                    </h2>
                    <p className="text-slate-300 leading-relaxed">
                      VyntraWeb accompagne les restaurants, snacks, coiffeurs, artisans et commerçants de Thionville avec des sites internet modernes, rapides et pensés pour améliorer leur visibilité locale sur Google. L’objectif est simple : aider chaque commerce à présenter ses services, rassurer ses visiteurs et faciliter les demandes de contact depuis un site clair, professionnel et adapté au mobile.
                    </p>
                  </div>

                  <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6">
                    <h3 className="text-xl font-bold text-white mb-4">
                      Nos zones d’intervention en Moselle
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        ["Création de site internet à Thionville", "/creation-site-internet-thionville"],
                        ["Création de site internet à Hayange", "/creation-site-internet-hayange"],
                        ["Création de site internet à Florange", "/creation-site-internet-florange"],
                        ["Création de site internet à Yutz", "/creation-site-internet-yutz"],
                        ["Création de site internet à Terville", "/creation-site-internet-terville"],
                        ["Création de site internet à Clouange", "/creation-site-internet-clouange"],
                        ["Création de site internet à Rombas", "/creation-site-internet-rombas"],
                      ].map(([label, href]) => (
                        <Link
                          key={href}
                          to={href}
                          className="text-sm text-slate-300 hover:text-cyan-400 bg-slate-900/70 border border-slate-700 hover:border-cyan-400/50 rounded-xl px-4 py-3 transition-colors"
                        >
                          {label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <div className="h-12"></div>
          <Suspense fallback={<SectionFallback />}>
            <GoogleReviewsSection />
          </Suspense>
          <div className="h-12"></div>
          <Suspense fallback={<SectionFallback />}>
            <ContactSection />
          </Suspense>
          <div className="h-12"></div>
          <Suspense fallback={<SectionFallback />}>
            <FAQSection />
          </Suspense>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default HomePage;
